/**
 * effectiveNextServicePrice — single source of truth for the price the
 * operator should charge on the next billable visit.
 *
 *   active(override)  → override.amount
 *   else recurrence   → recurrence.amount
 *   else              → null  (operator must type manually)
 *
 * Pure read function — no mutation, no I/O. Reused by:
 *   • LeadServicePlanCard / ServicePlanCard rate row
 *   • Upcoming work order surfaces (chip)
 *   • ChargeNowModal passive callout
 *
 * Override is considered "active" only when present AND `consumedAt` is
 * falsy. A consumed override is treated as historical and falls through to
 * the recurring rate. Missing / malformed values gracefully degrade.
 */

export function isOverrideActive(override) {
  if (!override) return false;
  if (override.consumedAt) return false;
  const n = Number(override.amount);
  if (!Number.isFinite(n) || n < 0) return false;
  return true;
}

/**
 * @param {object} recurrence  lead.recurrence (Firestore map). May be null.
 * @returns {{ amount: number, source: 'override' | 'recurrence' } | null}
 */
export function effectiveNextServicePrice(recurrence) {
  const ovr = recurrence?.nextServiceOverride;
  if (isOverrideActive(ovr)) {
    return { amount: Number(ovr.amount), source: 'override' };
  }
  const rate = Number(recurrence?.amount);
  if (Number.isFinite(rate) && rate >= 0) {
    return { amount: rate, source: 'recurrence' };
  }
  return null;
}

export default effectiveNextServicePrice;
